import { EnergyEnvelope, type PresenceState, rms } from "./energy";
import { createParticles, drawHudRing, drawMorph, drawOrb, orbRadius, type Particle } from "./orb";
import { DEFAULT_PRIMARY, paletteFrom, type PresencePalette } from "./palette";
import { drawWaveform } from "./waveform";
import { stripExpressiveTag } from "../voice/tags";

export { rms } from "./energy";
export type { PresenceState } from "./energy";

/**
 * Stratul de prezență: orbul + waveform-ul, desenate pe două canvasuri fixe la
 * baza ferestrei. Portat din `docs/prototypes/presence.html`.
 *
 * `mount()` primește un shadow root oarecare - clasa nu presupune NIMIC despre
 * cine îl deține. `widget.ts` îi dă, deliberat, un shadow root PROPRIU, pe o
 * gazdă FRATE cu `.aig-root`, nu descendentă a lui: `.aig-root` e el însuși
 * `position: fixed`, iar unele situri (demo3/Shopify) îi pun un `transform`
 * din CSS-ul paginii - un `transform` pe orice strămoș devine containing block
 * pentru descendenții `position: fixed`, inclusiv peste granița shadow DOM
 * (calculul se face pe arborele aplatizat). Dacă orbul ar fi copil al
 * `.aig-root`, „fixed jos-centru" s-ar calcula față de `.aig-root`
 * transformat, nu față de fereastră. Ca gazdă-frate, canvasurile rămân
 * `position: fixed` față de fereastră indiferent ce face pagina cu `.aig-root`
 * - poziționarea centrată din prototip se păstrează identic peste tot.
 *
 * Regula din brand kit pe care o respectă bucla: **nu desenăm degeaba**. rAF-ul
 * pornește doar când stratul e vizibil și se oprește la `document.hidden` sau la
 * `hide()` - un canvas care se animează la infinit în colțul paginii altcuiva e
 * un cost de baterie pe care nu-l plătim noi.
 */

const ORB_W = 520;
const ORB_H = 240;
const WAVE_H = 110;
/** Distanța de la baza ferestrei până la centrul orbului. */
const ORB_BOTTOM_OFFSET = 52;

/**
 * Raza maximă a orbului chiar în `speaking` - starea în care apare și
 * caption-ul, deci ea contează, nu repausul. `orbRadius()` (orb.ts) e
 * `baseRadius * (1 + depth*sin(breath) + 0.1*energy)`: `depth` urcă la
 * `0.024` (`0.02*(1+0.2*sin(...))`, la vârful propriu), `sin(breath)` la `1`,
 * iar `energy` e plafonată la `1` chiar în `EnergyEnvelope.step`
 * (`energy.ts`: `Math.min(1, this.realLevel * REAL_GAIN)` / `ceiling` pe
 * bursturi). Chemăm funcția reală, nu o formulă duplicată aici, ca cele două
 * să nu poată diverge tăcut dacă cineva schimbă `orbRadius`.
 * `baseRadius` = 15, valoarea implicită - nimeni n-o suprascrie azi
 * (`new Presence(...)` din `widget.ts`/`orbchat/index.ts` nu trece
 * `baseRadius`).
 */
const ORB_RADIUS_SPEAKING_MAX = orbRadius(15, Math.PI / 2, 1, Math.PI / 2 / 0.133);

/**
 * Multiplicatorul razei halo-ului: `drawOrb` (orb.ts) desenează gradientul cu
 * `createRadialGradient(cx, cy, radius*0.6, cx, cy, radius*1.85)`, iar ultimul
 * color-stop (la fracția 1, adică exact `radius*1.85`) e transparent -
 * dincolo de el gradientul repetă acel stop, deci nu mai iese nimic vizibil.
 * `radius*2.7` (raza arcului umplut) e doar bufferul de umplere, nu geometrie
 * vizibilă. Dacă `drawOrb` schimbă gradientul, valoarea de-aici trebuie ținută
 * sincron manual - n-are de unde să fie importată, e literal în corpul funcției.
 */
const ORB_HALO_RADIUS_MULT = 1.85;

/**
 * Distanța de la baza ferestrei până la cel mai înalt pixel vizibil al orbului
 * (halo-ul, la energie maximă în `speaking`).
 *
 * Exportat fiindcă e ACELAȘI prag pentru orice plutește peste ansamblul orbului
 * - caption-ul de aici și bula de voce din orb-chat (`orbchat/card.ts`). Fiecare
 * își adaugă propriul termen de scurgere vizuală, dar geometria orbului se
 * calculează o singură dată. Aripile waveform-ului nu intră în calcul: formula
 * lui (`waveform.ts`, vârf ~15px peste centrul de la `ORB_BOTTOM_OFFSET`) dă un
 * maxim sub ăsta, deci orbul rămâne geometria de limpezit.
 */
export const ORB_VISUAL_TOP = ORB_BOTTOM_OFFSET + ORB_RADIUS_SPEAKING_MAX * ORB_HALO_RADIUS_MULT;

/**
 * Cât de departe se întinde efectiv sub propria muchie de jos glow-ul
 * caption-ului: `text-shadow: 0 0 18px rgba(255,255,255,.85)` (regula
 * `.aig-presence-caption` de mai jos) nu se taie la marginea cutiei - un blur
 * CSS iese simetric în jurul fiecărui glif, inclusiv sub ultimul rând. Fără
 * termenul ăsta, `bottom` garantează doar unde se oprește CUTIA, nu unde se
 * stinge vizual lumina ei - și tocmai lumina aia e ce s-a suprapus peste orb
 * pe ecranul lui Radu.
 */
const CAPTION_GLOW_BLEED = 18;

/**
 * Aer curat între ce plutește deasupra și cel mai înalt pixel al orbului.
 * Exportat din același motiv ca `ORB_VISUAL_TOP`: bula de voce vrea aceeași
 * respirație ca și caption-ul, nu una reglată separat.
 */
export const CAPTION_AIR = 16;

/**
 * Distanța de la baza ferestrei până la muchia de JOS a bulei de caption.
 *
 * Era `WAVE_H + 14` (124px): trata ÎNĂLȚIMEA CANVASULUI waveform-ului
 * (`WAVE_H` = 110px) ca proxy pentru vârful lui - un plafon convenabil, dar
 * niciodată calculat. Rezultatul live (ecranul lui Radu): bula acoperea
 * complet orbul. Cauza nu era orbul propriu-zis (halo-ul lui, calculat mai
 * sus, se oprește la ~83px) - era glow-ul PROPRIU al caption-ului
 * (`CAPTION_GLOW_BLEED`), care iese sub cutie și, la doar 124-110=14px liberi
 * deasupra unui plafon el însuși aproximativ, ajungea vizual peste orb.
 *
 * Acum: `ORB_VISUAL_TOP` (halo-ul orbului la energie maximă în `speaking`,
 * calculat din `orbRadius()` real) + `CAPTION_GLOW_BLEED` (cât iese glow-ul
 * caption-ului sub propria cutie) + `CAPTION_AIR` (16px liberi, cerut
 * 12-16px). Formula waveform-ului (`waveform.ts`: `energy*15*fall*...`, vârf
 * ~15px față de centrul lui) dă un maxim mult sub cel al orbului, deci orbul
 * rămâne geometria de limpezit.
 */
export const CAPTION_BOTTOM_OFFSET = Math.ceil(ORB_VISUAL_TOP + CAPTION_GLOW_BLEED + CAPTION_AIR);

/**
 * Geometria cardului în care se morfează orbul (orb-chat, `orbchat/`). Stă aici
 * fiindcă bucla de desen o folosește; DOM-ul cardului o importă ca să nu existe
 * două adevăruri despre aceeași formă.
 */
export const CARD_WIDTH = 340;
export const CARD_MAX_HEIGHT = 140;
/** Înălțimea formei cât timp e încă orb - din prototip. */
const CARD_MIN_HEIGHT = 48;
/** Cât rămâne între baza ferestrei și muchia de jos a cardului. */
export const CARD_BOTTOM_OFFSET = 18;
/** Sub atât conținutul nu mai încape: cardul nu se strânge sub un rând. */
const CARD_FLOOR = 44;
/** Aerul dintre text și marginea de sticlă, sus + jos. */
export const CARD_PADDING = 26;

/**
 * Cine spune buclei ce formă să aibă: e desfăcut cardul ACUM și cât cere
 * conținutul lui. Deliberat două funcții, nu două numere - starea trăiește în
 * `orbchat/`, iar bucla o citește la fiecare cadru, fără să fie notificată.
 */
export interface MorphSource {
  open(): boolean;
  /** Înălțimea cerută de conținut, în px CSS (fără aer - îl adaugă bucla). */
  height(): number;
}

const PRESENCE_CSS = `
/* The presence layer draws into the viewport, not into the launcher's corner: these
   canvases are position:fixed inside the shadow root, so they resolve against the
   window even though :host sits bottom-right. Nothing here may capture the pointer -
   the visitor has to be able to click the page underneath the glow. */
/* --aig-lift: the merchant's escape hatch for a page chrome element sitting under the
   ensemble (data-bottom-offset on the script tag - see sdk/index.ts and orbchat/index.ts).
   Read with a 0px fallback so a shadow tree where nobody ever sets it (the classic
   widget's own presence host) renders byte-identical to before this existed. */
.aig-presence { position: fixed !important; pointer-events: none !important; z-index: 2147482990 !important;
  opacity: 0; transition: opacity .35s ease; }
.aig-presence--on { opacity: 1; }
.aig-presence-orb { left: 50%; bottom: var(--aig-lift, 0px); width: ${ORB_W}px; height: ${ORB_H}px; margin-left: -${ORB_W / 2}px; }
.aig-presence-wave { left: 0; right: 0; bottom: var(--aig-lift, 0px); width: 100%; height: ${WAVE_H}px; }
/* In voice mode the orb IS the chat, so the last line has to be readable over
   whatever page it floats above - hence the shadow rather than a panel. It sits
   above the orb and never captures the pointer: the visitor is still browsing a
   real site underneath. */
.aig-presence-caption { position: fixed !important; left: 50%; bottom: calc(${CAPTION_BOTTOM_OFFSET}px + var(--aig-lift, 0px)); width: min(520px, 84vw);
  transform: translateX(-50%); z-index: 2147482991 !important; pointer-events: none !important;
  text-align: center; font-size: 15px; line-height: 1.5; font-weight: 500; color: #1c2430;
  text-shadow: 0 1px 2px rgba(255,255,255,.9), 0 0 18px rgba(255,255,255,.85);
  opacity: 0; transition: opacity .3s; }
.aig-presence-caption--on { opacity: 1; }
/* The visitor's own words read as quieter than the assistant's: they already
   know what they said, it is there to confirm they were heard. */
.aig-presence-caption--user { font-size: 13.5px; font-weight: 400; color: #6b7280; font-style: italic; }
@media (prefers-reduced-motion: reduce) {
  /* Motion is the whole point of this layer, so reduced motion does not mean a frozen
     orb - it means we never mount it at all (see shouldRender). This rule only covers
     the case where the preference flips while the layer is already up. */
  .aig-presence { transition: none; }
}
`;

export interface PresenceOptions {
  /** Culoarea proprietarului; paleta se derivă din ea. */
  primaryColor?: string;
  /** Raza orbului în repaus. 15px = valoarea din prototip. */
  baseRadius?: number;
  /**
   * Stratul ăsta E suprafața de conversație, nu decorul de peste ea (orb-chat).
   *
   * Diferența e tot ce contează la `hide()`. Peste panoul clasic, prezența e un
   * strat în plus, aprins doar cât ține modul de voce - retragerea lui la Stop
   * e corectă, panoul rămâne pe ecran. În orb-chat orbul e ÎNSUȘI widgetul:
   * `hide()` acolo îl duce la `opacity: 0` și nu-l mai reaprinde nimeni, deci
   * vizitatorul rămâne cu o pagină goală și fără nimic de apăsat.
   *
   * Exact asta s-a întâmplat pe 10 aug 2026: după un `open_search` rostit,
   * conversația se relua pe `/search`, iar butonul care încheia vocea ștergea
   * tot widgetul (`voice/controller.ts` → `stop()`). Cu flagul pornit, `hide()`
   * curăță doar captionul și lasă orbul acolo unde e - vezi `voiceOrbSearch.test.ts`.
   */
  alwaysVisible?: boolean;
  /**
   * Inelul de instrument din jurul orbului - pielea `hud`, aprinsă mereu de
   * orb-chat (`orbchat/index.ts`). Stins, bucla desenează exact ce desena
   * înainte: calea clasică (`widget.ts`) nu-l aprinde niciodată.
   */
  hudRing?: boolean;
}

export class Presence {
  private orbCanvas?: HTMLCanvasElement;
  private waveCanvas?: HTMLCanvasElement;
  private orbCtx: CanvasRenderingContext2D | null = null;
  private waveCtx: CanvasRenderingContext2D | null = null;
  private caption?: HTMLDivElement;

  private readonly palette: PresencePalette;
  private readonly baseRadius: number;
  private readonly energy = new EnergyEnvelope();
  private readonly particles: Particle[] = createParticles();

  private state: PresenceState = "idle";
  private visible = false;
  private raf = 0;
  private prev = 0;
  private breath = 0;
  private presence = 0;
  private focus = 0;
  private waveWidth = 0;
  /** Nul pe calea clasică: fără el, bucla desenează exact orbul de până acum. */
  private morphSource?: MorphSource;
  private morph = 0;
  private cardH = CARD_MIN_HEIGHT;

  private readonly onResize = (): void => this.resize();
  private readonly onVisibility = (): void => {
    if (document.hidden) this.stopLoop();
    else if (this.visible) this.startLoop();
  };

  /** Vezi `PresenceOptions.alwaysVisible` - stratul e suprafața, nu decorul. */
  private readonly alwaysVisible: boolean;

  /** Vezi `PresenceOptions.hudRing`. */
  private readonly hudRing: boolean;

  constructor(opts: PresenceOptions = {}) {
    this.palette = paletteFrom(opts.primaryColor || DEFAULT_PRIMARY);
    this.baseRadius = opts.baseRadius ?? 15;
    this.alwaysVisible = opts.alwaysVisible === true;
    this.hudRing = opts.hudRing === true;
  }

  /**
   * Randarea se sare complet dacă vizitatorul a cerut mai puțină mișcare sau
   * dacă browserul n-are canvas 2D (jsdom în teste, medii exotice). În ambele
   * cazuri restul widgetului funcționează neschimbat - stratul ăsta e în plus,
   * niciodată o condiție.
   */
  static shouldRender(): boolean {
    if (typeof document === "undefined") return false;
    if (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    return true;
  }

  mount(shadow: ShadowRoot): void {
    if (!Presence.shouldRender()) return;

    const style = document.createElement("style");
    style.setAttribute("data-ai-guidance", "presence-css");
    style.textContent = PRESENCE_CSS;
    shadow.appendChild(style);

    this.orbCanvas = document.createElement("canvas");
    this.orbCanvas.className = "aig-presence aig-presence-orb";
    this.waveCanvas = document.createElement("canvas");
    this.waveCanvas.className = "aig-presence aig-presence-wave";
    // `aria-hidden`: e decor legat de starea anunțată deja textual de panou.
    // Un cititor de ecran n-are ce face cu două canvasuri fără conținut.
    this.orbCanvas.setAttribute("aria-hidden", "true");
    this.waveCanvas.setAttribute("aria-hidden", "true");

    this.orbCtx = this.orbCanvas.getContext("2d");
    this.waveCtx = this.waveCanvas.getContext("2d");
    if (!this.orbCtx || !this.waveCtx) {
      // Fără context 2D nu avem ce desena. Ieșim curat, fără canvasuri orfane.
      this.orbCtx = null;
      this.waveCtx = null;
      return;
    }

    this.caption = document.createElement("div");
    this.caption.className = "aig-presence-caption";
    // `role="status"`: în modul de voce ăsta e singurul text al conversației,
    // deci un cititor de ecran trebuie să-l anunțe - spre deosebire de canvasuri,
    // care sunt decor.
    this.caption.setAttribute("role", "status");

    shadow.append(this.orbCanvas, this.waveCanvas, this.caption);
    this.resize();
    addEventListener("resize", this.onResize);
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  /** Aduce stratul în pagină și pornește bucla. */
  show(): void {
    if (!this.orbCtx) return;
    this.visible = true;
    this.orbCanvas?.classList.add("aig-presence--on");
    this.waveCanvas?.classList.add("aig-presence--on");
    if (!document.hidden) this.startLoop();
  }

  /**
   * Scoate stratul. Bucla se oprește după tranziția de opacitate, nu instant -
   * altfel orbul ar dispărea printr-un salt, exact opusul a ce vinde stratul.
   */
  hide(): void {
    // Suprafața nu se retrage niciodată: fără orb n-ar mai exista widget, iar
    // vizitatorul n-ar avea ce apăsa ca s-o ia de la capăt. Captionul, în
    // schimb, chiar trebuie să plece - replica vocală s-a încheiat.
    if (this.alwaysVisible) {
      this.clearCaption();
      return;
    }
    this.visible = false;
    this.orbCanvas?.classList.remove("aig-presence--on");
    this.waveCanvas?.classList.remove("aig-presence--on");
    this.clearCaption();
    setTimeout(() => {
      if (!this.visible) this.stopLoop();
    }, 400);
  }

  setState(state: PresenceState): void {
    this.state = state;
  }

  /**
   * Aprinde morful orb→card. Cât timp nimeni nu-l cheamă - adică pe toate
   * site-urile de azi - bucla e bit cu bit cea de dinainte: `drawOrb`, aceleași
   * valori, același contur.
   */
  attachMorph(source: MorphSource): void {
    this.morphSource = source;
  }

  /**
   * Mută stratul, cât timp ghidarea are nevoie de locul lui (vezi
   * `setChatObstacle` din sdk.ts). Transformul stă pe CANVASURI, niciodată pe
   * gazdă: un `transform` pe un strămoș devine containing block pentru
   * descendenții `position: fixed`, adică exact capcana pentru care stratul are
   * gazdă separată. Pe elementul fix însuși e doar o deplasare.
   */
  setOffset(dx: number, dy: number): void {
    const value = dx === 0 && dy === 0 ? "" : `translate(${dx}px, ${dy}px)`;
    if (this.orbCanvas) this.orbCanvas.style.transform = value;
    if (this.waveCanvas) this.waveCanvas.style.transform = value;
  }

  /**
   * Chiar desenează? `false` sub `prefers-reduced-motion` și în orice mediu
   * fără canvas 2D (jsdom). Orb-chatul îl întreabă ca să cadă pe varianta de
   * sticlă în CSS: flagul trebuie să funcționeze și acolo unde nu morfăm nimic.
   */
  isDrawing(): boolean {
    return this.orbCtx !== null;
  }

  /**
   * Ultima replică, arătată sub orb. În modul de voce ăsta e tot chatul: nu
   * există istoric derulabil, fiindcă o conversație vorbită nu are scrollback -
   * exact ce spune și promptul modelului (vezi `buildVoiceSegment`).
   *
   * Prin `textContent`, ca peste tot în widget: niciun HTML din transcript nu
   * ajunge randat.
   */
  setCaption(role: "user" | "agent", text: string): void {
    if (!this.caption) return;
    this.caption.className = `aig-presence-caption aig-presence-caption--on${
      role === "user" ? " aig-presence-caption--user" : ""
    }`;
    // Tagul expresiv e o direcție pentru sinteză, nu ceva de citit - vezi
    // `voice/tags.ts`. Se curăță la RANDARE, nu la sursă.
    this.caption.textContent = stripExpressiveTag(text);
  }

  clearCaption(): void {
    if (!this.caption) return;
    this.caption.className = "aig-presence-caption";
    this.caption.textContent = "";
  }

  /**
   * Nivelul audio real, din captare sau redare. Blocul e PCM float în [-1,1];
   * RMS-ul se calculează aici, ca apelantul să nu-l repete în trei locuri.
   */
  feedAudio(samples: Float32Array, now = performance.now()): void {
    this.energy.feed(rms(samples), now);
  }

  /** Varianta pentru cine are deja un nivel 0..1 calculat (ex. un AnalyserNode). */
  feedLevel(level: number, now = performance.now()): void {
    this.energy.feed(level, now);
  }

  destroy(): void {
    this.stopLoop();
    removeEventListener("resize", this.onResize);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.orbCanvas?.remove();
    this.waveCanvas?.remove();
    this.caption?.remove();
    this.orbCtx = null;
    this.waveCtx = null;
  }

  private startLoop(): void {
    if (this.raf || !this.orbCtx) return;
    this.prev = performance.now();
    const tick = (t: number): void => {
      this.frame(t);
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  private stopLoop(): void {
    if (!this.raf) return;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /**
   * Canvasul e desenat la rezoluția ecranului, dar măsurat în px CSS. DPR-ul e
   * plafonat la 2: pe un ecran 3x diferența nu se vede, iar costul de umplere
   * crește cu pătratul.
   */
  private resize(): void {
    const dpr = Math.min(devicePixelRatio || 1, 2);
    if (this.orbCanvas && this.orbCtx) {
      this.orbCanvas.width = ORB_W * dpr;
      this.orbCanvas.height = ORB_H * dpr;
      this.orbCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    if (this.waveCanvas && this.waveCtx) {
      this.waveWidth = this.waveCanvas.clientWidth || innerWidth;
      this.waveCanvas.width = this.waveWidth * dpr;
      this.waveCanvas.height = WAVE_H * dpr;
      this.waveCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  }

  private frame(t: number): void {
    const orbCtx = this.orbCtx;
    const waveCtx = this.waveCtx;
    if (!orbCtx || !waveCtx) return;
    const dt = t - this.prev;
    this.prev = t;
    const ts = t / 1000;

    const level = this.energy.step(this.state, t, dt);
    const smooth = (current: number, target: number, ms: number): number =>
      current + (target - current) * Math.min(1, Math.max(0, dt) / ms);

    // Prezența urcă lent și coboară lent: orbul nu „apare", ci se adună.
    this.presence = smooth(this.presence, this.visible ? 1 : 0, 800);
    this.focus = smooth(this.focus, this.state === "processing" ? 1 : 0, 500);
    // Respirația se strânge când e activ: 6s în repaus, 4s când are ceva de făcut.
    const period = 6 - 2 * this.presence;
    this.breath += ((Math.max(0, dt) / 1000) * Math.PI * 2) / period;

    orbCtx.clearRect(0, 0, ORB_W, ORB_H);
    const cx = ORB_W / 2;
    const cy = ORB_H - ORB_BOTTOM_OFFSET;
    const frame = {
      palette: this.palette,
      cx,
      cy,
      baseRadius: this.baseRadius,
      energy: level,
      breath: this.breath,
      ts,
      dtMs: dt,
      presence: this.presence,
      focus: this.focus,
      particles: this.particles,
    };
    // Aripile waveform-ului pornesc de la marginea formei din CADRUL CURENT, nu
    // de la raza de repaus - altfel intră sub ea când orbul se umflă pe o
    // silabă tare, iar cu cardul desfăcut ar porni din mijlocul sticlei.
    let edge: number;
    if (this.morphSource) {
      const open = this.morphSource.open();
      // 450ms morful, 220ms înălțimea: forma ajunge la destinație înaintea
      // conținutului care încă se așază, nu invers.
      this.morph = smooth(this.morph, open ? 1 : 0, 450);
      const wanted = open
        ? Math.max(CARD_FLOOR, Math.min(CARD_MAX_HEIGHT, this.morphSource.height() + CARD_PADDING))
        : CARD_MIN_HEIGHT;
      this.cardH = smooth(this.cardH, wanted, 220);
      edge = drawMorph(orbCtx, {
        ...frame,
        morph: this.morph,
        cardWidth: CARD_WIDTH,
        cardHeight: this.cardH,
        cardCy: ORB_H - CARD_BOTTOM_OFFSET - this.cardH / 2,
      });
    } else {
      drawOrb(orbCtx, frame);
      edge = orbRadius(this.baseRadius, this.breath, level, ts);
    }

    // Inelul se desenează doar în jurul unui ORB, niciodată în jurul unui card:
    // un cadran de instrument agățat de o cutie de text n-ar mai fi aparatura
    // orbului, ci un chenar. În pielea `hud` morful nici nu rulează (orb-chatul
    // nu-l mai atașează), deci pragul e o plasă, nu calea normală.
    if (this.hudRing && this.morph < 0.05) {
      drawHudRing(
        orbCtx,
        cx,
        cy,
        orbRadius(this.baseRadius, this.breath, level, ts),
        ts,
        level,
        this.palette,
      );
    }

    waveCtx.clearRect(0, 0, this.waveWidth, WAVE_H);
    drawWaveform(waveCtx, {
      palette: this.palette,
      cx: this.waveWidth / 2,
      cy: WAVE_H - ORB_BOTTOM_OFFSET,
      startOffset: edge + 4,
      half: Math.min(this.waveWidth / 2 - 30, 180),
      energy: level,
      t,
    });
  }
}
